import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { initTracker } from '@/modules/shared/lib/analytics/tracker'
import { useTrackPageView } from '@/modules/shared/hooks/useTrackPageView'
import { AuthProvider, useAuth } from './modules/shared/contexts/AuthContext'
import QueryProvider from './modules/shared/providers/QueryProvider'
import { PlatformProvider } from './platform'
import { ResponsiveShell } from './components/ResponsiveShell'
import type { QuotaDetail } from '@/modules/shared/components/modals/UpgradeModal'
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { I18nProvider, extractLanguageFromPath, buildLocalizedPath } from './i18n'
import { useLanguageStore } from './stores/languageStore'
import { useTheme } from '@/modules/shared/hooks/useTheme'
import { ErrorBoundary } from './components/ErrorBoundary'
import { CookieConsent } from '@/modules/shared/components/CookieConsent'

// 路由级代码分割 - 懒加载页面组件
// 已重构的页面 - 使用新模块路径
const FeedPage = lazy(() => import('./modules/social/pages/Feed'))
const MessagesPage = lazy(() => import('./modules/social/pages/Messages'))
const Notifications = lazy(() => import('./modules/social/pages/Notifications'))
const SearchPage = lazy(() => import('./modules/social/pages/Search'))
const UserProfilePage = lazy(() => import('./modules/social/pages/UserProfile'))
const TopicFeedNewPage = lazy(() => import('./modules/social/pages/TopicFeed'))
const PostDetailPage = lazy(() => import('./modules/social/pages/PostDetail'))
const ArticleDetailPage = lazy(() => import('./modules/social/pages/ArticleDetail'))
const FollowListPage = lazy(() => import('./modules/social/pages/FollowList'))

// Auth
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPassword'))

// Agent Mode - 已重构（使用默认导出）
const AgentHomepage = lazy(() => import('./modules/agent-mode/pages/AgentHomepage'))
const MyAgentPage = lazy(() => import('./modules/agent-mode/pages/MyAgent'))
const AgentProfilePage = lazy(() => import('./modules/agent-mode/pages/AgentProfile'))
const AgentChatPage = lazy(() => import('./modules/agent-mode/pages/AgentChat'))
const AgentDiscoveryPage = lazy(() => import('./modules/agent-mode/pages/AgentDiscovery'))
const PlazaPage = lazy(() => import('./modules/agent-mode/pages/Plaza'))
const MarketingPage = lazy(() => import('./modules/agent-mode/pages/MarketingPage'))
const ConversationViewerPage = lazy(() => import('./modules/agent-mode/pages/ConversationViewer'))

// Brand Dashboard - Phase 5
const BrandDashboard = lazy(() => import('./modules/brand-dashboard/pages/Dashboard'))
const BrandAnalytics = lazy(() => import('./modules/brand-dashboard/pages/Analytics'))
const BrandContentManager = lazy(() => import('./modules/brand-dashboard/pages/ContentManager'))
const BrandSettings = lazy(() => import('./modules/brand-dashboard/pages/BrandSettings'))


// Circles - P0
const CirclesPage = lazy(() => import('./pages/circles/CirclesPage'))
const CircleDetailPage = lazy(() => import('./pages/circles/CircleDetailPage'))
const CircleWelcomePage = lazy(() => import('./pages/circles/CircleWelcomePage'))
const MyCirclesPage = lazy(() => import('./pages/circles/MyCirclesPage'))

// Membership
const MembershipPage = lazy(() => import('./pages/membership/MembershipPage'))

// Creator Subscription (粉丝付费订阅创作者，区别于上面的平台会员)
const StudioHomePage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.StudioHomePage })),
)
const HelpHomePage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.HelpHomePage })),
)
const HelpArticlePage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.HelpArticlePage })),
)
const MonetizationHubPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.MonetizationHubPage })),
)
const EligibilityCheckPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.EligibilityCheckPage })),
)
const MySubscriptionsPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.MySubscriptionsPage })),
)
const StudioTierManagerPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.StudioTierManagerPage })),
)
const StudioMembersPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.StudioMembersPage })),
)
const StudioStatsPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.StudioStatsPage })),
)
const StudioPayoutsPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.StudioPayoutsPage })),
)
const AdminApprovalQueuePage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.AdminApprovalQueuePage })),
)
const AdminConfigManagerPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.AdminConfigManagerPage })),
)
const AdminStatsPage = lazy(() =>
  import('./modules/creator-subscription').then((m) => ({ default: m.AdminStatsPage })),
)

// Admin tip console
const AdminTipsPage = lazy(() => import('./modules/admin-tips/pages/AdminTipsPage'))
const AdminTipStatsPage = lazy(() => import('./modules/admin-tips/pages/AdminTipStatsPage'))

// Account Center
const AccountCenterPage = lazy(() => import('./modules/account/pages/AccountCenter'))

// Activities
const ActivityDetailPage = lazy(() => import('./pages/activities/ActivityDetailPage'))

// Settings & Help
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const HelpPage = lazy(() => import('./pages/help/HelpPage'))
const LegalPage = lazy(() => import('./pages/legal/LegalPage'))
const SuspendedPage = lazy(() => import('./pages/suspended/SuspendedPage'))

// Modals
const LoginModal = lazy(() =>
  import('@/modules/shared/components/Auth/LoginModal').then((m) => ({ default: m.LoginModal }))
)
const UpgradeModal = lazy(() =>
  import('@/modules/shared/components/modals/UpgradeModal').then((m) => ({ default: m.UpgradeModal }))
)

// 全局加载占位符
function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[var(--y-accent)] border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

/**
 * 语言路由布局组件
 * 处理语言检测和 URL 同步
 */
function LanguageLayout() {
  const location = useLocation()
  const { language, setLanguage } = useLanguageStore()
  
  // 从 URL 提取语言并同步到 store
  useEffect(() => {
    const urlLang = extractLanguageFromPath(location.pathname)
    if (urlLang && urlLang !== language) {
      setLanguage(urlLang)
    }
  }, [location.pathname, language, setLanguage])

  return <Outlet />
}

/**
 * 根路径重定向组件
 * 将 / 重定向到 /:lang/ 路径
 */
function RootRedirect() {
  const navigate = useNavigate()
  const { language } = useLanguageStore()
  const { authLoading, user } = useAuth()

  useEffect(() => {
    if (authLoading) return
    const target = user ? '/feed' : '/agent'
    navigate(buildLocalizedPath(language, target), { replace: true })
  }, [navigate, language, authLoading, user])

  return <PageLoading />
}

/**
 * 无语言路径重定向
 * 将 /agent, /feed 等重定向到 /:lang/agent, /:lang/feed
 */
function LanguageRedirect({ path }: { path: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { language } = useLanguageStore()
  
  useEffect(() => {
    const sourcePath = location.pathname || path
    const targetPath = buildLocalizedPath(language, sourcePath || path)
    navigate(targetPath, { replace: true })
  }, [navigate, language, path, location.pathname])
  
  return <PageLoading />
}

function AppContent() {
  useEffect(() => {
    initTracker()
  }, [])

  useTrackPageView()
  useTheme() // 激活暗色模式 .dark class
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [upgradeDetail, setUpgradeDetail] = useState<QuotaDetail | null>(null)
  const { user, completeOAuthLogin, completeOAuthWithToken } = useAuth()

  const navigate = useNavigate()
  const location = useLocation()

  // 所有 hooks 必须在 early return 之前声明
  const oauthHandled = useRef(false)
  const [hasRedirectedAfterLogin, setHasRedirectedAfterLogin] = useState(false)

  // OAuth callback 拦截 — 在路由匹配之前处理
  useEffect(() => {
    if (location.pathname !== '/auth/callback' || oauthHandled.current) return
    oauthHandled.current = true

    const code = new URLSearchParams(window.location.search).get('code')
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
    const accessToken = hashParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token')
    const expiresAt = hashParams.get('expires_at')

    ;(async () => {
      if (code) {
        await completeOAuthLogin(code)
      } else if (accessToken) {
        await completeOAuthWithToken(accessToken, undefined, {
          refreshToken: refreshToken || undefined,
          expiresAt: expiresAt ? parseInt(expiresAt, 10) : undefined,
        })
      }
      navigate('/', { replace: true })
    })()
  }, [location.pathname, completeOAuthLogin, completeOAuthWithToken, navigate])

  // 登录后重定向
  useEffect(() => {
    if (user) {
      setShowLoginModal(false)
      const langMatch = location.pathname.match(/^\/(en|zh-CN|ja)/)
      const langPrefix = langMatch ? langMatch[1] : 'en'

      // 优先跳回 ProtectedRoute 存的目标页（如 /en/agent/me）
      const savedRedirect = sessionStorage.getItem('login_redirect')
      if (savedRedirect) {
        sessionStorage.removeItem('login_redirect')
        if (!hasRedirectedAfterLogin) {
          navigate(savedRedirect, { replace: true })
          setHasRedirectedAfterLogin(true)
        }
        return
      }

      if (!hasRedirectedAfterLogin && (location.pathname === `/${langPrefix}` || location.pathname === `/${langPrefix}/` || location.pathname === `/${langPrefix}/agent` || location.pathname === `/${langPrefix}/agent/home`)) {
        navigate(`/${langPrefix}/feed`, { replace: true })
        setHasRedirectedAfterLogin(true)
      }
    } else {
      setHasRedirectedAfterLogin(false)
    }
  }, [user, navigate, hasRedirectedAfterLogin, location.pathname])

  useEffect(() => {
    const handler = () => setShowLoginModal(true)
    window.addEventListener('open-login-modal', handler as EventListener)
    return () => window.removeEventListener('open-login-modal', handler as EventListener)
  }, [])

  // Listen for 402 quota exceeded events from API client
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as QuotaDetail | undefined
      if (detail && !upgradeDetail) {
        setUpgradeDetail(detail)
      }
    }
    window.addEventListener('quota:exceeded', handler)
    return () => window.removeEventListener('quota:exceeded', handler)
  }, [upgradeDetail])

  // OAuth callback 时只显示 loading，不渲染路由（所有 hooks 已在上方声明）
  if (location.pathname === '/auth/callback') {
    return <PageLoading />
  }

  return (
    <div
      className="min-h-screen w-full antialiased bg-[var(--y-bg)] text-[var(--y-text-primary)]"
      style={{
        background: 'radial-gradient(circle at top, var(--y-accent) 0%, var(--y-bg) 50vh)',
        minHeight: '100vh',
      }}
    >
      {showLoginModal && (
        <Suspense fallback={null}>
          <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </Suspense>
      )}
      {upgradeDetail && (
        <Suspense fallback={null}>
          <UpgradeModal detail={upgradeDetail} onClose={() => setUpgradeDetail(null)} />
        </Suspense>
      )}
      <CookieConsent />
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* 根路径重定向到默认语言 */}
          <Route path="/" element={<RootRedirect />} />

          {/* 无语言前缀的路径重定向 */}
          <Route path="/agent" element={<LanguageRedirect path="/agent" />} />
          <Route path="/agent/*" element={<LanguageRedirect path="/agent" />} />
          <Route path="/feed" element={<LanguageRedirect path="/feed" />} />
          <Route path="/messages" element={<LanguageRedirect path="/messages" />} />
          <Route path="/messages/:chatId" element={<LanguageRedirect path="/messages" />} />
          <Route path="/notifications" element={<LanguageRedirect path="/notifications" />} />
          <Route path="/search" element={<LanguageRedirect path="/search" />} />
          <Route path="/user/:id" element={<LanguageRedirect path="/user" />} />
          <Route path="/topic/:topicId" element={<LanguageRedirect path="/topic" />} />
          <Route path="/post/:id" element={<LanguageRedirect path="/post" />} />
          <Route path="/articles/:id" element={<LanguageRedirect path="/articles" />} />
          <Route path="/brand" element={<LanguageRedirect path="/brand" />} />
          <Route path="/brand/*" element={<LanguageRedirect path="/brand" />} />
          <Route path="/circles" element={<LanguageRedirect path="/circles" />} />
          <Route path="/circles/:slug" element={<LanguageRedirect path="/circles" />} />
          <Route path="/circles/:slug/welcome" element={<LanguageRedirect path="/circles" />} />
          <Route path="/activities/:id" element={<LanguageRedirect path="/activities" />} />
          <Route path="/membership" element={<LanguageRedirect path="/membership" />} />
          <Route path="/me/circles" element={<LanguageRedirect path="/me/circles" />} />
          <Route path="/me/subscriptions" element={<LanguageRedirect path="/me/subscriptions" />} />
          <Route path="/me/monetization" element={<LanguageRedirect path="/me/monetization" />} />
          <Route path="/me/monetization/*" element={<LanguageRedirect path="/me/monetization" />} />
          <Route path="/studio" element={<LanguageRedirect path="/studio" />} />
          <Route path="/studio/*" element={<LanguageRedirect path="/studio" />} />
          <Route path="/help/creator-studio" element={<LanguageRedirect path="/help/creator-studio" />} />
          <Route path="/help/creator-studio/*" element={<LanguageRedirect path="/help/creator-studio" />} />
          <Route path="/admin/subscription/*" element={<LanguageRedirect path="/admin/subscription" />} />
          <Route path="/admin/tips" element={<LanguageRedirect path="/admin/tips" />} />
          <Route path="/admin/tips/*" element={<LanguageRedirect path="/admin/tips" />} />
          <Route path="/settings" element={<LanguageRedirect path="/settings" />} />
          <Route path="/help" element={<LanguageRedirect path="/help" />} />
          <Route path="/privacy" element={<LanguageRedirect path="/privacy" />} />
          <Route path="/terms" element={<LanguageRedirect path="/terms" />} />
          <Route path="/child-safety" element={<LanguageRedirect path="/child-safety" />} />
          <Route path="/suspended-accounts" element={<LanguageRedirect path="/suspended-accounts" />} />
          <Route path="/disclaimer" element={<LanguageRedirect path="/disclaimer" />} />
          <Route path="/suspended" element={<LanguageRedirect path="/suspended" />} />

          {/* Auth pages (outside shell) */}
          <Route path="/auth/callback" element={<Suspense fallback={<PageLoading />}><OAuthCallback /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<PageLoading />}><ResetPasswordPage /></Suspense>} />

          {/* Main app routes — wrapped in ResponsiveShell */}
          <Route path="/:lang" element={<LanguageLayout />}>
            {/* Full-screen pages (no navigation shell) */}
            <Route path="suspended" element={<Suspense fallback={<PageLoading />}><SuspendedPage /></Suspense>} />
            <Route path="circles/:slug/welcome" element={<Suspense fallback={<PageLoading />}><CircleWelcomePage /></Suspense>} />

            <Route element={<ResponsiveShellOutlet />}>
              <Route index element={<AuthFallback />} />
              {/* Agent 路由 */}
              <Route path="agent" element={<AgentHomepage />} />
              <Route path="agent/home" element={<AgentHomepage />} />
              <Route path="agent/plaza" element={<PlazaPage />} />
              <Route path="agent/discovery" element={<AgentDiscoveryPage />} />
              <Route path="agent/me" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MyAgentPage />
                </ProtectedRoute>
              } />
              <Route path="agent/marketing" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MarketingPage />
                </ProtectedRoute>
              } />
              <Route path="agent/me/conversation/:otherId" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <ConversationViewerPage />
                </ProtectedRoute>
              } />
              <Route path="agent/:agentId" element={<AgentProfilePage />} />
              <Route path="agent/:agentId/chat" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AgentChatPage />
                </ProtectedRoute>
              } />

              {/* Social 页面 */}
              <Route path="feed" element={<FeedPage />} />
              <Route path="messages" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MessagesPage />
                </ProtectedRoute>
              } />
              <Route path="messages/:chatId" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MessagesPage />
                </ProtectedRoute>
              } />
              <Route path="notifications" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="search" element={<SearchPage />} />
              <Route path="user/:id" element={<UserProfilePage />} />
              <Route path="user/:id/followers" element={<FollowListPage />} />
              <Route path="user/:id/following" element={<FollowListPage />} />
{/* TODO: /u/:username route — needs backend username→userId resolution API first */}
              <Route path="topic/:topicId" element={<TopicFeedNewPage />} />
              <Route path="post/:id" element={<PostDetailPage />} />
              <Route path="articles/:id" element={<ArticleDetailPage />} />

              {/* Brand Dashboard - Phase 5 */}
              <Route path="brand" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <BrandDashboard />
                </ProtectedRoute>
              } />
              <Route path="brand/analytics" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <BrandAnalytics />
                </ProtectedRoute>
              } />
              <Route path="brand/content" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <BrandContentManager />
                </ProtectedRoute>
              } />
              <Route path="brand/settings" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <BrandSettings />
                </ProtectedRoute>
              } />

              {/* Membership */}
              <Route path="membership" element={<MembershipPage />} />

              {/* Creator Subscription - Fan / Studio / Admin */}
              <Route path="me/monetization" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MonetizationHubPage />
                </ProtectedRoute>
              } />
              <Route path="me/monetization/:program" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <EligibilityCheckPage />
                </ProtectedRoute>
              } />
              <Route path="me/subscriptions" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MySubscriptionsPage />
                </ProtectedRoute>
              } />
              <Route path="studio" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <StudioHomePage />
                </ProtectedRoute>
              } />
              <Route path="help/creator-studio" element={<HelpHomePage />} />
              <Route path="help/creator-studio/:slug" element={<HelpArticlePage />} />
              <Route path="studio/subscriptions/tiers" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <StudioTierManagerPage />
                </ProtectedRoute>
              } />
              <Route path="studio/subscriptions/members" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <StudioMembersPage />
                </ProtectedRoute>
              } />
              <Route path="studio/subscriptions/stats" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <StudioStatsPage />
                </ProtectedRoute>
              } />
              <Route path="studio/subscriptions/payouts" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <StudioPayoutsPage />
                </ProtectedRoute>
              } />
              <Route path="admin/subscription/approvals" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AdminApprovalQueuePage />
                </ProtectedRoute>
              } />
              <Route path="admin/subscription/config" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AdminConfigManagerPage />
                </ProtectedRoute>
              } />
              <Route path="admin/subscription/stats" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AdminStatsPage />
                </ProtectedRoute>
              } />
              <Route path="admin/tips" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AdminTipsPage />
                </ProtectedRoute>
              } />
              <Route path="admin/tips/stats" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <AdminTipStatsPage />
                </ProtectedRoute>
              } />
              <Route
                path="account"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                      <AccountCenterPage />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="privacy" element={<LegalPage />} />
              <Route path="terms" element={<LegalPage />} />
              <Route path="child-safety" element={<LegalPage />} />
              <Route path="suspended-accounts" element={<LegalPage />} />
              <Route path="disclaimer" element={<LegalPage />} />

              {/* Circles - P0 */}
              <Route path="circles" element={<CirclesPage />} />
              <Route path="circles/:slug" element={<CircleDetailPage />} />
              <Route path="me/circles" element={
                <ProtectedRoute user={user} onRequireAuth={() => setShowLoginModal(true)}>
                  <MyCirclesPage />
                </ProtectedRoute>
              } />
              <Route path="activities/:id" element={<ActivityDetailPage />} />

              {/* 未匹配路由 - 按认证状态分流 */}
              <Route path="*" element={<AuthFallback />} />
            </Route>
          </Route>

          {/* 捕获所有其他路径 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}

function ResponsiveShellOutlet() {
  return (
    <ResponsiveShell>
      <Outlet />
    </ResponsiveShell>
  )
}

function App() {
  return (
    <QueryProvider>
      <PlatformProvider>
        <AuthProvider>
          <I18nProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </I18nProvider>
        </AuthProvider>
      </PlatformProvider>
    </QueryProvider>
  )
}

export default App

// TODO: ProfileRoute — needs backend username→userId resolution API
// Uncomment when /api/users/by-username/:username endpoint exists
// function ProfileRoute() { return <UserProfilePage /> }

// 未匹配路由兜底 - 按认证状态分流（带语言前缀）
function AuthFallback() {
  const { authLoading } = useAuth()
  const { language } = useLanguageStore()
  if (authLoading) return <PageLoading />
  return <Navigate to={buildLocalizedPath(language, '/feed')} replace />
}

// 受保护路由组件
function ProtectedRoute({
  children,
  user,
  onRequireAuth
}: {
  children: React.ReactNode
  user: ReturnType<typeof useAuth>['user']
  onRequireAuth: () => void
}) {
  const { authLoading } = useAuth()
  const hasTriggeredLogin = useRef(false)
  const { lang } = useParams()
  const location = useLocation()

  useEffect(() => {
    if (!authLoading && !user && !hasTriggeredLogin.current) {
      hasTriggeredLogin.current = true
      // 记住用户想去的完整 URL（含 query/hash），登录后跳回
      sessionStorage.setItem('login_redirect', location.pathname + location.search + location.hash)
      onRequireAuth()
    }
  }, [authLoading, user, onRequireAuth, location.pathname, location.search, location.hash])

  if (authLoading) {
    return <PageLoading />
  }

  if (!user) {
    return <Navigate to={`/${lang || 'en'}/feed`} replace />
  }

  return <>{children}</>
}
